import type { JSX, ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface ContentHeaderProps {
	readonly actions?: ReactNode;
	readonly className?: string;
	/** Extra header body (e.g. tab list). Renders under title when both are set. */
	readonly content?: ReactNode;
	/** `tabs`: header sized to the tab strip, triggers bottom-aligned on the divider. */
	readonly contentVariant?: "default" | "tabs";
	readonly description?: ReactNode;
	readonly title?: ReactNode;
}

const HeaderIdentity = ({
	description,
	title,
}: {
	readonly description?: ReactNode;
	readonly title?: ReactNode;
}): JSX.Element => (
	<div className="min-w-0 flex-1">
		{title ? <h1 className="truncate font-medium text-[15px] tracking-tight">{title}</h1> : null}
		{description ? <p className="mt-0.5 truncate text-[13px] text-muted-foreground">{description}</p> : null}
	</div>
);

/**
 * Header inside the boxed content panel.
 * Must sit above page body with a bottom divider.
 * `min-h-12` keeps tab headers even with title-only headers.
 * When `content` is set (tabs), it spans full width so line borders stretch edge-to-edge.
 */
export default function ContentHeader({
	title,
	description,
	actions,
	content,
	contentVariant = "default",
	className,
}: ContentHeaderProps): JSX.Element | null {
	if (!(title || description || actions || content)) {
		return null;
	}

	if (content) {
		const isTabs = contentVariant === "tabs";
		const hasIdentity = Boolean(title || description);
		return (
			<header
				className={cn(
					"flex w-full shrink-0 flex-col items-stretch border-border border-b",
					isTabs || hasIdentity ? null : "min-h-[70px]",
					className
				)}
				data-slot="content-header">
				{hasIdentity ? (
					<div className="flex w-full min-w-0 items-start justify-between gap-3 px-4 pt-3 pb-1">
						<HeaderIdentity description={description} title={title} />
						{actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
					</div>
				) : null}
				<div className={cn("flex w-full min-w-0", isTabs ? "items-end" : "min-h-full items-stretch")}>{content}</div>
				{hasIdentity || !actions ? null : (
					<div className="flex shrink-0 flex-wrap items-center gap-2 px-4 py-2">{actions}</div>
				)}
			</header>
		);
	}

	return (
		<header
			className={cn(
				"flex min-h-[70px] shrink-0 flex-wrap items-center justify-between gap-3 border-border border-b px-4 py-3",
				className
			)}
			data-slot="content-header">
			<HeaderIdentity description={description} title={title} />
			{actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
		</header>
	);
}
